import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import ReactTooltip from 'react-tooltip'

import Box from '@material-ui/core/Box'
import Icon from '@material-ui/core/Icon'
import Grid from '@material-ui/core/Grid'
import Input from '@material-ui/core/Input'
import Tooltip from '@material-ui/core/Tooltip'
import Checkbox from '@material-ui/core/Checkbox'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'

import makeStyles from '@material-ui/core/styles/makeStyles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useLocation } from 'react-router-dom'

import { useEndorsement } from '../../../shared/contexts/EndorsementContext'
import useYup from '../../../shared/hooks/useYup'
import useUserClient from '../../../clients/UserClient/useUserClient'
import {
  ButtonLight,
  ButtonGradient,
} from '../../../shared/components'

import {
  ENDORSEMENT_URL_EXECUTIVE_REQUEST,
  COLOR_SUCCESS,
} from '../../../constants'

import { GlobalMessages } from '../../../shared/messages'
import PhoneInput from '../../../shared/components/PhoneInput'
import CollapsePrice from '../../../shared/components/CollapsePrice/CollapsePrice'

import useSecurity from '../../../security/useSecurity'
import { useSecurityAction } from '../../../store/ducks/security'

import { ReactComponent as IcoWhats } from '../../../assets/img/icons/ico-whats.svg'

import RequesterData from '../../../models/PolicyModels/EndorsementRequesterData'

const useStyles = makeStyles((theme) => ({
  buttonsStep: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& .MuiBox-root': {
      minWidth: 190,
      [theme.breakpoints.down('xs')]: {
        marginTop: 5,
        marginLeft: 5,
        minWidth: 115,
      },
    },
  },
  label: {
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
  },
  labelObject: {
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  whatsApplabel: {
    marginTop: 3,
    color: theme.palette.text.quaternary,
    fontWeight: 'bold',
  },
  whatsappRow: {
    display: 'flex',
    alignItems: 'center',
  },
  whatsappIcon: {
    width: 22,
    height: 22,
    marginRight: 8,
    flexShrink: 0,
  },
  checkboxCheked: {
    '& .MuiCheckbox-colorSecondary.Mui-checked': {
      color: COLOR_SUCCESS,
    },
  },
}))

const Requester = forwardRef((props, ref) => {
  const classes = useStyles()
  const {
    checked,
    handleStep,
    handleNext,
    handlePrevious,
  } = props
  const { phoneOrCellphone } = useYup()

  const {
    onNext,
    actionNext,
    endorsement,
    setEndorsement,
  } = useEndorsement()

  const {
    getName,
    getEmail,
    getPhone,
    getUser,
  } = useSecurity()

  const { setUser } = useSecurityAction()

  const userClient = useUserClient()

  const [openTooltip, setOpenTooltip] = useState(false)
  const [clientEmails, setClientEmails] = useState([])

  const toggleClick = (bool) => {
    setOpenTooltip(bool)
  }

  const location = useLocation()

  const getUrl = location?.pathname?.split(':')
  const routeExecutiveRequest = getUrl[0]
  const email = (ENDORSEMENT_URL_EXECUTIVE_REQUEST !== routeExecutiveRequest) ? getEmail() : ''
  const name = (ENDORSEMENT_URL_EXECUTIVE_REQUEST !== routeExecutiveRequest) ? getName() : ''
  const phone = (ENDORSEMENT_URL_EXECUTIVE_REQUEST !== routeExecutiveRequest) ? getPhone() : ''
  useEffect(() => {
    const hasPolicy = endorsement && endorsement.endorsementRequest
    && endorsement.endorsementRequest.policy

    if ((ENDORSEMENT_URL_EXECUTIVE_REQUEST === routeExecutiveRequest) && hasPolicy) {
      const policyHolder = endorsement.endorsementRequest.policy.policyHolderDocument

      userClient().findUserEmailsByClient(policyHolder).then((response) => {
        setClientEmails(response.data)
      })
    }
  }, [
    userClient,
    endorsement,
    endorsement?.endorsementRequest,
    endorsement?.endorsementRequest?.policy,
    routeExecutiveRequest,
  ])

  const initialValuesForm = useMemo(() => ({
    ...Object.assign(
      RequesterData({
        Name: endorsement?.isEditing ? endorsement?.requester?.name : name,
        Email: endorsement?.isEditing ? endorsement?.requester?.email : email,
        Note: endorsement?.requester?.note ?? '',
        Phone: endorsement?.isEditing ? endorsement?.requester?.phone : phone,
        CustomerControlIdentifier: endorsement?.requester?.customerControlIdentifier ?? '',
      }),
    ),
    sendWhatsapp: !!(endorsement?.requester?.mobilePhone),
    mobilePhone: endorsement?.requester?.mobilePhone ?? getUser()?.mobilePhone ?? '',
  }), [name, endorsement, email, phone, getUser])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesForm,
    validationSchema: Yup.object({
      phone: phoneOrCellphone.required(),
      name: Yup.string().max(200).required(),
      email: Yup.string().email().required(),
      customerControlIdentifier: Yup.string().max(100),
      sendWhatsapp: Yup.boolean(),
      mobilePhone: Yup.string().when('sendWhatsapp', (sendWhatsapp, schema) => (
        sendWhatsapp ? phoneOrCellphone.required() : schema
      )),
    }),
    onSubmit: (data) => {
      const { sendWhatsapp, mobilePhone, ...rest } = data
      const cleanPhone = mobilePhone?.replace(/[^0-9]/g, '')

      const requesterData = {
        ...rest,
        ...(sendWhatsapp && cleanPhone ? { mobilePhone: cleanPhone } : {}),
      }

      const values = {
        ...endorsement,
        ...{ requester: { ...requesterData } },
      }
      setEndorsement(values)

      if (sendWhatsapp && cleanPhone) {
        const currentUser = getUser() || {}
        const userMobilePhone = (currentUser.mobilePhone ?? '').replace(/[^0-9]/g, '')
        if (cleanPhone !== userMobilePhone) {
          setUser({ ...currentUser, mobilePhone: cleanPhone })
        }
        userClient().updateContactPhone(cleanPhone)
      }

      if (actionNext) {
        onNext(values)
      }
    },
  })

  useImperativeHandle(ref, () => ({
    onSubmit: () => new Promise((resolve) => {
      formik.validateForm().then((errors) => {
        let data

        if (!isEmpty(errors)) {
          data = { message: GlobalMessages.VERIFIQUE_CAMPOS_DESTAQUE_ANTES_PROSSEGUIR }
        }

        resolve(data)
        formik.submitForm()
      })
    }),
  }))

  const requesterLabel = 'Nome do Solicitante'
  const phoneLabel = 'Telefone do Solicitante'
  const noteLabel = 'Observações'
  const idClientControlLabel = 'Identificador do Controle do Cliente'
  const clientEmailLabel = 'Email do Solicitante'

  const isExecutiveRoute = ENDORSEMENT_URL_EXECUTIVE_REQUEST === routeExecutiveRequest

  return (
    <>
      <CollapsePrice
        checked={checked}
        handleEdit={handleStep}
        hiddenEdit={!formik.isSubmitting}
        title="Solicitante"
        subtitle="Informe abaixo os dados do Solicitante"
      >
        <Grid container spacing={5}>
          <Grid item sm={4} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="requesterLabel" className={classes.label}>{requesterLabel}</label>
              <Input
                fullWidth
                id="name"
                color="secondary"
                disabled={!isExecutiveRoute && !!getName()}
                {...formik.getFieldProps('name')}
                error={formik.touched.name && !!formik.errors.name}
              />
              <FormHelperText
                hidden={!formik.touched.name || !formik.errors.name}
                error={formik.touched.name && !!formik.errors.name}
              >
                {formik.errors.name}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={4} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="phoneLabel" className={classes.label}>{phoneLabel}</label>
              <PhoneInput
                id="phone"
                phoneType="auto"
                placeholder="(00) 0000-0000"
                disabled={!isExecutiveRoute && !!getPhone()}
                {...formik.getFieldProps('phone')}
                error={formik.touched.phone && !!formik.errors.phone}
              />
              <FormHelperText
                hidden={!formik.touched.phone || !formik.errors.phone}
                error={formik.touched.phone && !!formik.errors.phone}
              >
                {formik.errors.phone}
              </FormHelperText>
            </FormControl>
          </Grid>
          {(ENDORSEMENT_URL_EXECUTIVE_REQUEST === routeExecutiveRequest)
          && (
          <Grid item sm={4} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="clientEmailLabel" className={classes.label}>{clientEmailLabel}</label>
              <Autocomplete
                id="email"
                onInputChange={(event, newValue) => {
                  formik.setFieldValue('email', newValue)
                }}
                options={clientEmails}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    error={formik.touched.email && !!formik.errors.email}
                  />
                )}
                {...formik.getFieldProps('email')}
              />
              <FormHelperText
                error={formik.touched.email && !!formik.errors.email}
              >
                {formik.errors.email}
              </FormHelperText>
            </FormControl>
          </Grid>
          )}

          <Grid item xs={12} sm={6} style={{ alignSelf: 'flex-start' }}>
            <Box className={`${classes.whatsappRow} ${classes.checkboxCheked}`}>
              <Checkbox
                checked={formik.values.sendWhatsapp}
                style={{ padding: 0 }}
                inputProps={{ 'aria-label': 'Deseja receber o PDF da apólice por WhatsApp?' }}
                onChange={(e) => {
                  formik.setFieldValue('sendWhatsapp', e.target.checked)
                  if (!e.target.checked) {
                    formik.setFieldTouched('mobilePhone', false)
                  }
                }}
              />
              <Box component="span" className={classes.label}>
                Deseja receber o PDF da apólice por WhatsApp?
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <Box component="span" className={classes.whatsApplabel}>
                Número de WhatsApp
              </Box>
              <PhoneInput
                id="mobilePhone"
                phoneType="auto"
                placeholder="(00) 0 0000-0000"
                disabled={!formik.values.sendWhatsapp}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IcoWhats fill={COLOR_SUCCESS} className={classes.whatsappIcon} />
                    </InputAdornment>
                  ),
                }}
                {...formik.getFieldProps('mobilePhone')}
                error={formik.touched.mobilePhone && !!formik.errors.mobilePhone}
              />
              <FormHelperText
                hidden={!formik.touched.mobilePhone || !formik.errors.mobilePhone}
                error={formik.touched.mobilePhone && !!formik.errors.mobilePhone}
              >
                {formik.errors.mobilePhone}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item lg={4} md={6} sm={6} xs={12}>
            <FormControl fullWidth>
              <Box display="flex" mb={1}>
                <label htmlFor="idClientControlLabel" className={classes.label}>
                  {idClientControlLabel}
                </label>
                <Box
                  mr={1}
                  onClick={() => toggleClick(true)}
                  onMouseOver={() => toggleClick(true)}
                >
                  <Tooltip
                    placement="right-end"
                    className={classes.info}
                    title="Número de Controle de Cliente (campo opcional)"
                    open={openTooltip}
                    onClose={() => toggleClick(false)}
                  >
                    <Icon
                      data-html
                      style={{ cursor: 'help', marginLeft: 5 }}
                    >
                      help_outline
                    </Icon>
                  </Tooltip>
                </Box>
              </Box>
              <Input
                fullWidth
                id="customerControlIdentifier"
                color="secondary"
                {...formik.getFieldProps('customerControlIdentifier')}
                error={formik.touched.customerControlIdentifier
                  && !!formik.errors.customerControlIdentifier}
              />
              <FormHelperText
                hidden={!formik.touched.customerControlIdentifier
                  || !formik.errors.customerControlIdentifier}
                error={formik.touched.customerControlIdentifier
                  && !!formik.errors.customerControlIdentifier}
              >
                {formik.errors.customerControlIdentifier}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={12} xs={12}>
            <FormControl fullWidth>
              <label htmlFor="noteLabel" className={classes.labelObject}>{noteLabel}</label>
              <TextField
                id="note"
                multiline
                variant="outlined"
                fullWidth
                minRows={3}
                {...formik.getFieldProps('note')}
                error={formik.touched.note && !!formik.errors.note}
              />
              <FormHelperText
                hidden={!formik.touched.note || !formik.errors.note}
                error={formik.touched.note && !!formik.errors.note}
              >
                {formik.errors.note}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box className={classes.buttonsStep}>
              <Box>
                <ButtonLight
                  onClick={handlePrevious}
                  title="Voltar"
                >
                  Voltar
                </ButtonLight>
              </Box>
              <Box ml={4}>
                <ButtonGradient
                  onClick={handleNext}
                  title="Continuar"
                >
                  Continuar
                </ButtonGradient>
              </Box>
            </Box>
            <ReactTooltip place="top" type="dark" />
          </Grid>
        </Grid>
      </CollapsePrice>
    </>
  )
})

Requester.propTypes = {
  checked: PropTypes.bool,
  children: PropTypes.any,
  handleStep: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  handlePrevious: PropTypes.func.isRequired,
}

Requester.defaultProps = {
  children: '',
  checked: false,
}

export default Requester
